/**
 * Builds the embed URL, iframe snippet and preview dimensions for the Embed Map
 * builder.
 *
 * Only params that differ from the embed's own defaults are written, so a
 * default embed stays a bare URL. Which params apply depends on the mode: a
 * recording is a single track with no routes panel and no highlights, and the
 * embed ignores those params, so emitting them would only produce noisier
 * copy-paste code.
 */

import { routeStyleParams, type EmbedRouteStyles } from './embedRouteStyle';

export type EmbedMode = 'hike' | 'recording';

/**
 * `overlay` floats the header controls and elevation profile over the map (the
 * original embed); `stacked` puts a title bar above the map and docks the
 * profile below it, so nothing covers the route.
 */
export type EmbedLayout = 'overlay' | 'stacked';

/** `dark` is the embed's original look; `light` suits a white page. */
export type EmbedTheme = 'dark' | 'light';

export interface EmbedOptions {
  showHighlights: boolean;
  showElevation: boolean;
  elevationMinimized: boolean;
  showRoutesPanel: boolean;
  routesPanelCollapsed: boolean;
  units: string;
  /** Optional so callers that predate them keep producing the same URL. */
  layout?: EmbedLayout;
  /** False stops the mouse wheel zooming the map, so it can't hijack page scroll. */
  scrollZoom?: boolean;
  /** Label the route's start and end A and B, on the map and the profile. */
  endpointLabels?: boolean;
  /** Add the country to those labels ("Clonegal, Ireland"). Only means anything with endpointLabels. */
  endpointLabelCountry?: boolean;
  /** Optional like the rest: an embed without one is dark. */
  theme?: EmbedTheme;
  /**
   * Colour, thickness and opacity per route type, as in the map's Customise route style dialog.
   * Only the types that differ from the embed's defaults reach the URL.
   */
  routeStyles?: EmbedRouteStyles;
}

export const DEFAULT_EMBED_HEIGHT = '500';
/** The preview pane can't usefully grow past this, whatever the user types. */
export const MAX_PREVIEW_HEIGHT = 600;

/** Whether a mode's embed renders the routes panel and highlight (POI) markers. */
export function supportsRoutePanels(mode: EmbedMode): boolean {
  return mode !== 'recording';
}

export function supportsHighlights(mode: EmbedMode): boolean {
  return mode !== 'recording';
}

export function embedParams(options: EmbedOptions, mode: EmbedMode = 'hike'): URLSearchParams {
  const params = new URLSearchParams();

  if (supportsHighlights(mode) && !options.showHighlights) params.set('highlights', '0');
  if (!options.showElevation) params.set('elevation', '0');
  if (options.elevationMinimized) params.set('elevation_min', '1');
  if (supportsRoutePanels(mode)) {
    if (!options.showRoutesPanel) params.set('routes_panel', '0');
    if (options.routesPanelCollapsed) params.set('routes_collapsed', '1');
  }
  if (options.units === 'imperial') params.set('units', 'imperial');
  if (options.layout === 'stacked') params.set('layout', 'stacked');
  if (options.scrollZoom === false) params.set('scroll_zoom', '0');
  if (options.theme === 'light') params.set('theme', 'light');
  // A recording is one track in the Recording style; a hike's routes are main, alt and spur.
  routeStyleParams(options.routeStyles, mode === 'recording' ? ['recording'] : ['main', 'alt', 'spur']).forEach(
    ([name, value]) => params.set(name, value),
  );
  if (options.endpointLabels) {
    params.set('ab', '1');
    if (options.endpointLabelCountry) params.set('ab_country', '1');
  }

  return params;
}

export function embedUrl(baseUrl: string, options: EmbedOptions, mode: EmbedMode = 'hike'): string {
  const queryString = embedParams(options, mode).toString();
  return queryString ? `${baseUrl}?${queryString}` : baseUrl;
}

/** A bare number means pixels; anything else (`100%`, `40rem`) is passed through. */
export function embedWidthAttribute(width: string): string {
  const value = (width || '').trim();
  if (!value) return '100%';
  return /^\d+$/.test(value) ? `${value}px` : value;
}

/** The iframe `height` attribute is numeric only, so strip any unit the user typed. */
export function embedHeightAttribute(height: string): string {
  const value = (height || '').trim();
  if (!value) return DEFAULT_EMBED_HEIGHT;
  return value.replace(/[^\d]/g, '') || DEFAULT_EMBED_HEIGHT;
}

export function previewHeight(height: string): string {
  const numeric = parseInt(embedHeightAttribute(height), 10);
  return Math.min(numeric, MAX_PREVIEW_HEIGHT).toString();
}

export function embedIframeCode(url: string, width: string, height: string): string {
  return `<iframe src="${url}" width="${embedWidthAttribute(width)}" height="${embedHeightAttribute(height)}" ` +
    'frameborder="0" allowfullscreen style="border:none;border-radius:8px;"></iframe>';
}
